/**
 * Hull Moving Average (HMA)
 *
 * Near-zero-lag moving average invented by Alan Hull.
 * Algorithm: WMA(2 × WMA(n/2) − WMA(n), √n).
 * Significantly more responsive than SMA/EMA.
 *
 * Category: Trend | Pane: Overlay
 */
import { IndicatorDefinition, PlotData } from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";
import { getSource } from "./sma";

export const hmaIndicator: IndicatorDefinition = {
  id: "hma",
  name: "Hull Moving Average",
  shortName: "HMA",
  category: "trend",
  pane: "overlay",
  params: [
    { key: "period", label: "Period", type: "number", default: 14, min: 2, max: 500 },
    { key: "source", label: "Source", type: "select", default: "close", options: [
      { label: "Close", value: "close" }, { label: "Open", value: "open" },
      { label: "High", value: "high" }, { label: "Low", value: "low" },
      { label: "HL2", value: "hl2" }, { label: "HLC3", value: "hlc3" },
    ]},
    { key: "color", label: "Color", type: "color", default: "#00BCD4" },
  ],
  plots: [
    { key: "hma", label: "HMA", color: "#00BCD4", style: "line", lineWidth: 2 },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const { period, source = "close" } = params;
    const src = getSource(candles, source);

    const halfPeriod = Math.max(1, Math.floor(period / 2));
    const sqrtPeriod = Math.max(1, Math.round(Math.sqrt(period)));

    // WMA helper
    const computeWMA = (values: number[], len: number): number[] => {
      const result: number[] = [];
      const denom = (len * (len + 1)) / 2;
      for (let i = 0; i < values.length; i++) {
        if (i < len - 1) {
          result.push(NaN);
          continue;
        }
        let sum = 0;
        for (let j = 0; j < len; j++) {
          sum += values[i - len + 1 + j] * (j + 1);
        }
        result.push(sum / denom);
      }
      return result;
    };

    const wmaHalf = computeWMA(src, halfPeriod);
    const wmaFull = computeWMA(src, period);

    // Diff series: 2 * WMA(n/2) - WMA(n)
    const diff: number[] = [];
    for (let i = 0; i < src.length; i++) {
      if (isNaN(wmaHalf[i]) || isNaN(wmaFull[i])) {
        diff.push(NaN);
      } else {
        diff.push(2 * wmaHalf[i] - wmaFull[i]);
      }
    }

    // Filter NaN, compute WMA(sqrt(n)) on diff
    const validDiff = diff.filter(v => !isNaN(v));
    const hmaValues = computeWMA(validDiff, sqrtPeriod);

    const data: { time: number; value: number }[] = [];
    const startIdx = diff.findIndex(v => !isNaN(v));
    let validIdx = 0;

    for (let i = startIdx; i < candles.length; i++) {
      if (!isNaN(diff[i])) {
        if (validIdx < hmaValues.length && !isNaN(hmaValues[validIdx])) {
          data.push({ time: candles[i].time, value: hmaValues[validIdx] });
        }
        validIdx++;
      }
    }

    return [{ key: "hma", data }];
  },
};
